import { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router';
import {
    Box, Typography, Button, Stack, Chip, CircularProgress,
    Input, Modal, ModalDialog, ModalClose, IconButton, Alert,
    Checkbox, Tooltip,
} from '@mui/joy';
import { motion, AnimatePresence } from 'framer-motion';

import ArrowBackRoundedIcon          from '@mui/icons-material/ArrowBackRounded';
import AddRoundedIcon                from '@mui/icons-material/AddRounded';
import SearchRoundedIcon             from '@mui/icons-material/SearchRounded';
import EditRoundedIcon               from '@mui/icons-material/EditRounded';
import DeleteRoundedIcon             from '@mui/icons-material/DeleteRounded';
import SecurityRoundedIcon           from '@mui/icons-material/SecurityRounded';
import ShieldRoundedIcon             from '@mui/icons-material/ShieldRounded';
import LockRoundedIcon               from '@mui/icons-material/LockRounded';
import PeopleRoundedIcon             from '@mui/icons-material/PeopleRounded';
import ExpandMoreRoundedIcon         from '@mui/icons-material/ExpandMoreRounded';
import ExpandLessRoundedIcon         from '@mui/icons-material/ExpandLessRounded';
import CheckRoundedIcon              from '@mui/icons-material/CheckRounded';
import ContentCopyRoundedIcon        from '@mui/icons-material/ContentCopyRounded';
import GroupAddRoundedIcon           from '@mui/icons-material/GroupAddRounded';

import Header from '../../components/Header';
import { AppContext } from '../../context';
import {
    APIGetRoles,
    APIGetRoleDetail,
    APICreateRole,
    APIUpdateRole,
    APIDeleteRole,
    APIGetPermissions,
    parseApiError,
} from '../../api';
import MemeLoader from '../../components/MemeLoader';
import AssignRoleModal from './AssignRoleModal';


// ─── Категории прав (для группировки) ────────────────────────────────────────
const CATEGORY_CONFIG = {
    finance:   { label: 'Финансы',     color: '#22c55e', bg: 'rgba(34,197,94,0.1)',   icon: '💰' },
    schedule:  { label: 'График',      color: '#3b82f6', bg: 'rgba(59,130,246,0.1)',  icon: '📅' },
    employees: { label: 'Сотрудники',  color: '#f59e0b', bg: 'rgba(245,158,11,0.1)', icon: '👥' },
    locations: { label: 'Точки',       color: '#06b6d4', bg: 'rgba(6,182,212,0.1)',   icon: '📍' },
    roles:     { label: 'Роли',        color: '#a855f7', bg: 'rgba(168,85,247,0.1)',  icon: '🛡️' },
    reports:   { label: 'Отчёты',      color: '#ec4899', bg: 'rgba(236,72,153,0.1)',  icon: '📊' },
    system:    { label: 'Система',     color: '#6366f1', bg: 'rgba(99,102,241,0.1)',  icon: '⚙️' },
};

// ─── Анимации ─────────────────────────────────────────────────────────────────
const pageVariants = {
    initial: { opacity: 0, y: 16 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.38, ease: [0.22, 1, 0.36, 1] } },
    exit:    { opacity: 0, y: -12, transition: { duration: 0.22 } },
};

const stagger = {
    hidden:  {},
    visible: { transition: { staggerChildren: 0.055 } },
};

const fadeUp = {
    hidden:  { opacity: 0, y: 12 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] } },
};

// ─── Поле формы ───────────────────────────────────────────────────────────────
function FormField({ label, children, error }) {
    return (
        <Box>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: error ? '#f87171' : 'var(--joy-palette-text-secondary)', mb: 0.6 }}>
                {label}
            </Typography>
            {children}
            {error && (
                <Typography sx={{ fontSize: 11, color: '#f87171', mt: 0.4, fontWeight: 600 }}>
                    {error}
                </Typography>
            )}
        </Box>
    );
}

// ─── Компонент: строка роли ──────────────────────────────────────────────────
function RoleRow({ role, canCreate, canUpdate, canDelete, onEdit, onDelete, onDuplicate, canAssign, onAssign }) {
    const isSystem = role.is_system;

    return (
        <motion.div variants={fadeUp}>
            <Box sx={{
                display:      'flex',
                alignItems:   'center',
                gap:          1.5,
                px:           2,
                py:           1.5,
                borderRadius: 'var(--joy-radius-xl)',
                border:       '1px solid var(--joy-palette-divider)',
                bgcolor:      'var(--joy-palette-background-level1)',
                transition:   'all .15s',
                '&:hover':    { borderColor: 'rgba(162,38,226,0.3)', bgcolor: 'var(--joy-palette-background-level2)' },
            }}>
                {/* Иконка */}
                <Box sx={{
                    width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    bgcolor: isSystem ? 'rgba(245,158,11,0.1)' : 'rgba(162,38,226,0.1)',
                    border: `1.5px solid ${isSystem ? 'rgba(245,158,11,0.25)' : 'var(--joy-palette-divider)'}`,
                }}>
                    {isSystem
                        ? <ShieldRoundedIcon sx={{ fontSize: 18, color: '#f59e0b' }} />
                        : <SecurityRoundedIcon sx={{ fontSize: 18, color: 'var(--joy-palette-primary-400)' }} />
                    }
                </Box>

                {/* Инфо */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'var(--joy-palette-text-primary)', lineHeight: 1.2 }}>
                            {role.name}
                        </Typography>

                        {isSystem && (
                            <Chip size="sm" sx={{ bgcolor: 'rgba(245,158,11,0.1)', color: '#f59e0b', fontSize: 10, fontWeight: 700, border: 'none' }}>
                                Системная
                            </Chip>
                        )}
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2, mt: 0.4, flexWrap: 'wrap' }}>
                        <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-tertiary)', display: 'flex', alignItems: 'center', gap: 0.4 }}>
                            <LockRoundedIcon sx={{ fontSize: 11 }} />
                            {role.permissions_count ?? 0} прав
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-tertiary)', display: 'flex', alignItems: 'center', gap: 0.4 }}>
                            <PeopleRoundedIcon sx={{ fontSize: 11 }} />
                            {role.accounts_count ?? 0} сотр.
                        </Typography>
                        {role.description && (
                            <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-tertiary)' }}>
                                {role.description}
                            </Typography>
                        )}
                    </Box>
                </Box>

                {/* Кнопки */}
                <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                    {canAssign && (
                        <Tooltip title="Назначить сотрудникам" size="sm">
                            <IconButton
                                size="sm" variant="soft"
                                onClick={() => onAssign(role)}
                                sx={{
                                    bgcolor: 'rgba(34,197,94,0.08)',
                                    borderRadius: 'var(--joy-radius-md)',
                                    '&:hover': { bgcolor: 'rgba(34,197,94,0.18)' },
                                }}
                            >
                                <GroupAddRoundedIcon sx={{ fontSize: 18, color: '#22c55e' }} />
                            </IconButton>
                        </Tooltip>
                    )}

                    {canCreate && (
                        <Tooltip title="Дублировать" size="sm">
                            <IconButton
                                size="sm" variant="soft"
                                onClick={() => onDuplicate(role)}
                                sx={{
                                    bgcolor: 'rgba(99,102,241,0.08)',
                                    borderRadius: 'var(--joy-radius-md)',
                                    '&:hover': { bgcolor: 'rgba(99,102,241,0.18)' },
                                }}
                            >
                                <ContentCopyRoundedIcon sx={{ fontSize: 18, color: '#6366f1' }} />
                            </IconButton>
                        </Tooltip>
                    )}

                    {canUpdate && (
                        <IconButton
                            size="sm" variant="soft"
                            onClick={() => onEdit(role)}
                            sx={{
                                bgcolor: 'rgba(162,38,226,0.08)',
                                borderRadius: 'var(--joy-radius-md)',
                                '&:hover': { bgcolor: 'rgba(162,38,226,0.18)' },
                            }}
                        >
                            <EditRoundedIcon sx={{ fontSize: 18, color: 'var(--joy-palette-primary-300)' }} />
                        </IconButton>
                    )}

                    {canDelete && !isSystem && (
                        <IconButton
                            size="sm" variant="soft"
                            onClick={() => onDelete(role)}
                            sx={{
                                bgcolor: 'rgba(248,113,113,0.08)',
                                borderRadius: 'var(--joy-radius-md)',
                                '&:hover': { bgcolor: 'rgba(248,113,113,0.18)' },
                            }}
                        >
                            <DeleteRoundedIcon sx={{ fontSize: 18, color: '#f87171' }} />
                        </IconButton>
                    )}
                </Box>
            </Box>
        </motion.div>
    );
}

// ─── Компонент: категория прав (аккордеон) ───────────────────────────────────
function PermissionCategory({ category, permissions, selected, onToggle, onToggleAll }) {
    const [open, setOpen] = useState(false);
    const cfg = CATEGORY_CONFIG[category] || CATEGORY_CONFIG.system;
    const allSelected  = permissions.every(p => selected.has(p.id));
    const someSelected = permissions.some(p => selected.has(p.id));
    const count        = permissions.filter(p => selected.has(p.id)).length;

    return (
        <Box sx={{
            borderRadius: 'var(--joy-radius-lg)',
            border: '1px solid var(--joy-palette-divider)',
            bgcolor: 'var(--joy-palette-background-level1)',
            overflow: 'hidden',
        }}>
            {/* Заголовок категории */}
            <Box
                onClick={() => setOpen(v => !v)}
                sx={{
                    display: 'flex', alignItems: 'center', gap: 1.5,
                    px: 2, py: 1.2,
                    cursor: 'pointer', userSelect: 'none',
                    transition: 'background .12s',
                    '&:hover': { bgcolor: 'var(--joy-palette-background-level2)' },
                }}
            >
                <Box sx={{
                    fontSize: 16, width: 28, height: 28, borderRadius: '8px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    bgcolor: cfg.bg,
                }}>
                    {cfg.icon}
                </Box>

                <Typography sx={{ flex: 1, fontSize: 13, fontWeight: 700, color: 'var(--joy-palette-text-primary)' }}>
                    {cfg.label}
                </Typography>

                <Chip size="sm" sx={{
                    bgcolor: count > 0 ? cfg.bg : 'transparent',
                    color: count > 0 ? cfg.color : 'var(--joy-palette-text-tertiary)',
                    fontSize: 10, fontWeight: 700, border: 'none',
                }}>
                    {count}/{permissions.length}
                </Chip>

                {open
                    ? <ExpandLessRoundedIcon sx={{ fontSize: 18, color: 'var(--joy-palette-text-tertiary)' }} />
                    : <ExpandMoreRoundedIcon sx={{ fontSize: 18, color: 'var(--joy-palette-text-tertiary)' }} />
                }
            </Box>

            {/* Список прав */}
            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        style={{ overflow: 'hidden' }}
                    >
                        <Box sx={{ px: 2, pb: 1.5, pt: 0.5 }}>
                            {/* Выбрать все */}
                            <Box
                                onClick={() => onToggleAll(permissions.map(p => p.id), !allSelected)}
                                sx={{
                                    display: 'flex', alignItems: 'center', gap: 1,
                                    py: 0.7, px: 1, mb: 0.5,
                                    borderRadius: 'var(--joy-radius-sm)',
                                    cursor: 'pointer', userSelect: 'none',
                                    '&:hover': { bgcolor: 'rgba(162,38,226,0.06)' },
                                }}
                            >
                                <Checkbox
                                    size="sm"
                                    checked={allSelected}
                                    indeterminate={someSelected && !allSelected}
                                    readOnly
                                    sx={{ '--Checkbox-size': '16px' }}
                                />
                                <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'var(--joy-palette-primary-300)' }}>
                                    Выбрать все
                                </Typography>
                            </Box>

                            <Stack spacing={0}>
                                {permissions.map(perm => (
                                    <Box
                                        key={perm.id}
                                        onClick={() => onToggle(perm.id)}
                                        sx={{
                                            display: 'flex', alignItems: 'center', gap: 1,
                                            py: 0.7, px: 1,
                                            borderRadius: 'var(--joy-radius-sm)',
                                            cursor: 'pointer', userSelect: 'none',
                                            transition: 'background .1s',
                                            '&:hover': { bgcolor: 'rgba(162,38,226,0.06)' },
                                        }}
                                    >
                                        <Checkbox
                                            size="sm"
                                            checked={selected.has(perm.id)}
                                            readOnly
                                            sx={{ '--Checkbox-size': '16px' }}
                                        />
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'var(--joy-palette-text-primary)', lineHeight: 1.3 }}>
                                                {perm.name}
                                            </Typography>
                                            <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)', fontFamily: 'monospace' }}>
                                                {perm.tag}
                                            </Typography>
                                        </Box>
                                    </Box>
                                ))}
                            </Stack>
                        </Box>
                    </motion.div>
                )}
            </AnimatePresence>
        </Box>
    );
}

// ─── Модалка: создать / редактировать роль ───────────────────────────────────
function RoleModal({ open, role, permissions, onClose, onSave, loading, error }) {
    const isEdit = !!role;

    const [name, setName]               = useState('');
    const [description, setDescription] = useState('');
    const [selected, setSelected]       = useState(new Set());
    const [permSearch, setPermSearch]    = useState('');
    const [errs, setErrs]               = useState({});

    useEffect(() => {
        if (!open) return;
        setErrs({});
        setPermSearch('');
        if (role) {
            setName(role.name || '');
            setDescription(role.description || '');
            setSelected(new Set((role.permissions || []).map(p => p.id)));
        } else {
            setName('');
            setDescription('');
            setSelected(new Set());
        }
    }, [open, role]);

    const toggle = (id) => {
        setSelected(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const toggleAll = (ids, add) => {
        setSelected(prev => {
            const next = new Set(prev);
            ids.forEach(id => add ? next.add(id) : next.delete(id));
            return next;
        });
    };

    const validate = () => {
        const e = {};
        if (!name.trim()) e.name = 'Укажите название роли';
        return e;
    };

    // Группировка прав по категориям
    const grouped = {};
    const q = permSearch.toLowerCase();
    permissions.forEach(p => {
        if (q && !p.name.toLowerCase().includes(q) && !p.tag.toLowerCase().includes(q)) return;
        const cat = p.category || 'system';
        if (!grouped[cat]) grouped[cat] = [];
        grouped[cat].push(p);
    });

    // Сортировка категорий
    const categoryOrder = ['employees', 'finance', 'schedule', 'reports', 'locations', 'roles', 'system'];
    const sortedCategories = categoryOrder.filter(c => grouped[c]?.length > 0);

    return (
        <Modal open={open} onClose={onClose}>
            <ModalDialog sx={{
                bgcolor: 'var(--joy-palette-background-level1)',
                border: '1px solid var(--joy-palette-divider)',
                borderRadius: 'var(--joy-radius-xl)',
                boxShadow: '0 24px 64px rgba(0,0,0,0.7)',
                maxWidth: 560, width: '100%',
                p: 3, maxHeight: '90vh', overflowY: 'auto',
            }}>
                <ModalClose sx={{ color: 'var(--joy-palette-text-tertiary)' }} />

                <Box sx={{ mb: 2.5 }}>
                    <Typography sx={{ fontSize: 18, fontWeight: 800, letterSpacing: '-0.01em', color: 'var(--joy-palette-text-primary)' }}>
                        {isEdit ? '✏️ Редактировать роль' : '🛡️ Новая роль'}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-tertiary)', mt: 0.4 }}>
                        {isEdit ? 'Измените название и набор прав' : 'Создайте роль и выберите права доступа'}
                    </Typography>
                </Box>

                <Stack spacing={1.8}>
                    <FormField label="Название роли *" error={errs.name}>
                        <Input
                            value={name}
                            onChange={e => { setName(e.target.value); setErrs(p => ({ ...p, name: undefined })); }}
                            placeholder="Например: Старший фотограф"
                            sx={{ ...(errs.name && { borderColor: '#f87171' }) }}
                        />
                    </FormField>

                    <FormField label="Описание">
                        <Input
                            value={description}
                            onChange={e => setDescription(e.target.value)}
                            placeholder="Необязательно"
                        />
                    </FormField>

                    {/* Права */}
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'var(--joy-palette-text-secondary)' }}>
                                Права доступа
                            </Typography>
                            <Chip size="sm" sx={{
                                bgcolor: selected.size > 0 ? 'rgba(162,38,226,0.1)' : 'transparent',
                                color: selected.size > 0 ? 'var(--joy-palette-primary-300)' : 'var(--joy-palette-text-tertiary)',
                                fontSize: 10, fontWeight: 700, border: 'none',
                            }}>
                                {selected.size} из {permissions.length}
                            </Chip>
                        </Box>

                        <Input
                            value={permSearch}
                            onChange={e => setPermSearch(e.target.value)}
                            placeholder="Поиск прав…"
                            startDecorator={<SearchRoundedIcon sx={{ fontSize: 14, color: 'var(--joy-palette-text-tertiary)' }} />}
                            sx={{ mb: 1.5, fontSize: 12 }}
                        />

                        <Stack spacing={1}>
                            {sortedCategories.map(cat => (
                                <PermissionCategory
                                    key={cat}
                                    category={cat}
                                    permissions={grouped[cat]}
                                    selected={selected}
                                    onToggle={toggle}
                                    onToggleAll={toggleAll}
                                />
                            ))}
                        </Stack>

                        {sortedCategories.length === 0 && (
                            <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-tertiary)', textAlign: 'center', py: 2 }}>
                                {permSearch ? 'Ничего не найдено' : 'Нет доступных прав'}
                            </Typography>
                        )}
                    </Box>

                    {error && (
                        <Alert color="danger" variant="soft" size="sm" sx={{ borderRadius: 'var(--joy-radius-md)', fontSize: 12 }}>
                            {error}
                        </Alert>
                    )}

                    <Box sx={{ display: 'flex', gap: 1, pt: 0.5 }}>
                        <Button
                            onClick={() => {
                                const e = validate();
                                setErrs(e);
                                if (Object.keys(e).length === 0) {
                                    onSave({ name, description, permission_ids: [...selected] });
                                }
                            }}
                            disabled={loading}
                            loading={loading}
                            sx={{
                                flex: 1,
                                background: 'linear-gradient(135deg, var(--joy-palette-primary-500) 0%, var(--joy-palette-primary-300) 100%)',
                                color: '#fff', fontWeight: 700, border: 'none',
                                boxShadow: '0 4px 16px rgba(162,38,226,0.35)',
                                '&:hover': { boxShadow: '0 6px 24px rgba(162,38,226,0.5)' },
                            }}
                        >
                            {isEdit ? 'Сохранить' : 'Создать роль'}
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={onClose}
                            sx={{
                                borderColor: 'var(--joy-palette-divider)',
                                color: 'var(--joy-palette-text-secondary)',
                                fontWeight: 600,
                                '&:hover': { bgcolor: 'var(--joy-palette-background-level2)' },
                            }}
                        >
                            Отмена
                        </Button>
                    </Box>
                </Stack>
            </ModalDialog>
        </Modal>
    );
}

// ─── Модалка: удаление роли ──────────────────────────────────────────────────
function DeleteRoleModal({ open, role, onClose, onConfirm, loading }) {
    return (
        <Modal open={open} onClose={onClose}>
            <ModalDialog sx={{
                bgcolor: 'var(--joy-palette-background-level1)',
                border: '1px solid rgba(248,113,113,0.3)',
                borderRadius: 'var(--joy-radius-xl)',
                boxShadow: '0 24px 64px rgba(0,0,0,0.7)',
                maxWidth: 380, width: '100%', p: 3,
            }}>
                <ModalClose sx={{ color: 'var(--joy-palette-text-tertiary)' }} />

                <Box sx={{ textAlign: 'center', mb: 2.5 }}>
                    <Box sx={{
                        width: 52, height: 52, borderRadius: '50%',
                        bgcolor: 'rgba(248,113,113,0.12)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        mx: 'auto', mb: 1.5,
                    }}>
                        <DeleteRoundedIcon sx={{ fontSize: 24, color: '#f87171' }} />
                    </Box>
                    <Typography sx={{ fontSize: 16, fontWeight: 800, color: 'var(--joy-palette-text-primary)' }}>
                        Удалить роль?
                    </Typography>
                    {role && (
                        <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-tertiary)', mt: 0.5 }}>
                            «{role.name}» будет удалена навсегда
                        </Typography>
                    )}
                    {role?.accounts_count > 0 && (
                        <Alert color="warning" variant="soft" size="sm" sx={{ mt: 1.5, borderRadius: 'var(--joy-radius-md)', fontSize: 11 }}>
                            Эта роль привязана к {role.accounts_count} сотрудникам. Сначала смените им роль.
                        </Alert>
                    )}
                </Box>

                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        onClick={onConfirm}
                        loading={loading}
                        disabled={role?.accounts_count > 0}
                        sx={{
                            flex: 1,
                            background: 'linear-gradient(135deg, #dc2626 0%, #f87171 100%)',
                            color: '#fff', fontWeight: 700, border: 'none',
                            boxShadow: '0 4px 16px rgba(248,113,113,0.35)',
                        }}
                    >
                        Удалить
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={onClose}
                        sx={{ borderColor: 'var(--joy-palette-divider)', color: 'var(--joy-palette-text-secondary)', fontWeight: 600 }}
                    >
                        Отмена
                    </Button>
                </Box>
            </ModalDialog>
        </Modal>
    );
}

// ════════════════════════════════════════════════════════════════════
//  RolesPage
// ════════════════════════════════════════════════════════════════════
export default function RolesPage() {
    const { token, account, showToast, hasPerm } = useContext(AppContext);
    const navigate = useNavigate();

    const canCreate = hasPerm('roles.create');
    const canUpdate = hasPerm('roles.update');
    const canDelete = hasPerm('roles.delete');
    const canAssign = hasPerm('roles.assign');

    const [roles,       setRoles]       = useState([]);
    const [permissions, setPermissions] = useState([]);
    const [loading,     setLoading]     = useState(true);
    const [search,      setSearch]      = useState('');

    // Модалки
    const [assignModal, setAssignModal] = useState({ open: false, role: null });
    const [editModal,   setEditModal]   = useState({ open: false, role: null, loading: false, error: '' });
    const [deleteModal, setDeleteModal] = useState({ open: false, role: null, loading: false });

    // ── Загрузка ──────────────────────────────────────────────────
    const loadData = useCallback(async () => {
        setLoading(true);
        const [rolesData, permsData] = await Promise.all([
            APIGetRoles(token),
            APIGetPermissions(token),
        ]);
        if (Array.isArray(rolesData)) setRoles(rolesData);
        if (Array.isArray(permsData)) setPermissions(permsData);
        setLoading(false);
    }, [token]);

    useEffect(() => { loadData(); }, [loadData]);

    // ── Фильтрация ───────────────────────────────────────────────
    const filtered = roles.filter(r => {
        const q = search.toLowerCase();
        return !q || r.name.toLowerCase().includes(q) || r.description?.toLowerCase().includes(q);
    });

    // ── Открыть редактирование (загрузить detail с правами) ──────
    const handleOpenEdit = async (role) => {
        setEditModal({ open: true, role: null, loading: true, error: '' });
        const detail = await APIGetRoleDetail(token, role.id);
        if (detail?.id) {
            setEditModal({ open: true, role: detail, loading: false, error: '' });
        } else {
            setEditModal({ open: true, role: { ...role, permissions: [] }, loading: false, error: '' });
        }
    };

    // ── Дублировать ──────────────────────────────────────────────
    const handleDuplicate = async (role) => {
        const detail = await APIGetRoleDetail(token, role.id);
        const dupeRole = {
            ...(detail || role),
            id: null,
            name: `${role.name} (копия)`,
            is_system: false,
        };
        setEditModal({ open: true, role: dupeRole, loading: false, error: '' });
    };

    // ── Сохранить ────────────────────────────────────────────────
    const handleSave = async (form) => {
        const isEdit = editModal.role?.id;
        setEditModal(prev => ({ ...prev, loading: true, error: '' }));

        let res;
        if (isEdit) {
            res = await APIUpdateRole(token, editModal.role.id, form);
        } else {
            res = await APICreateRole(token, form);
        }

        if (res?.error) {
            setEditModal(prev => ({
                ...prev, loading: false,
                error: parseApiError(res.error, 'Не удалось сохранить роль'),
            }));
            return;
        }

        showToast(isEdit ? 'Роль обновлена ✓' : 'Роль создана ✓');
        setEditModal({ open: false, role: null, loading: false, error: '' });
        loadData();
    };

    // ── Удалить ──────────────────────────────────────────────────
    const handleDelete = async () => {
        setDeleteModal(prev => ({ ...prev, loading: true }));
        const res = await APIDeleteRole(token, deleteModal.role.id);
        setDeleteModal({ open: false, role: null, loading: false });

        if (res?.error) {
            showToast(parseApiError(res.error, 'Ошибка удаления'), 'danger');
        } else {
            showToast('Роль удалена', 'warning');
            loadData();
        }
    };

    // ── Рендер ───────────────────────────────────────────────────
    return (
        <motion.div
            variants={pageVariants}
            initial="initial" animate="animate" exit="exit"
            style={{ minHeight: '100vh', background: 'var(--joy-palette-background-body)' }}
        >
            <Header />

            <Box sx={{ maxWidth: 780, mx: 'auto', px: { xs: 2, sm: 3 }, py: 3 }}>

                {/* Заголовок */}
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.04 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box
                                onClick={() => navigate('/manager')}
                                sx={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    width: 36, height: 36,
                                    borderRadius: 'var(--joy-radius-md)',
                                    border: '1px solid var(--joy-palette-divider)',
                                    bgcolor: 'var(--joy-palette-background-level1)',
                                    cursor: 'pointer', transition: 'all .15s',
                                    '&:hover': { borderColor: 'var(--joy-palette-primary-500)', bgcolor: 'rgba(162,38,226,0.08)' },
                                }}
                            >
                                <ArrowBackRoundedIcon sx={{ fontSize: 18, color: 'var(--joy-palette-text-secondary)' }} />
                            </Box>
                            <Box>
                                <Typography sx={{ fontSize: 20, fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--joy-palette-text-primary)' }}>
                                    Роли и права
                                </Typography>
                                <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-tertiary)', mt: 0.2 }}>
                                    {roles.length} {roles.length === 1 ? 'роль' : roles.length < 5 ? 'роли' : 'ролей'}
                                </Typography>
                            </Box>
                        </Box>

                        {canCreate && (
                            <Button
                                startDecorator={<AddRoundedIcon sx={{ fontSize: 16 }} />}
                                onClick={() => setEditModal({ open: true, role: null, loading: false, error: '' })}
                                sx={{
                                    background: 'linear-gradient(135deg, var(--joy-palette-primary-500) 0%, var(--joy-palette-primary-300) 100%)',
                                    color: '#fff', fontWeight: 700, fontSize: 13,
                                    border: 'none',
                                    boxShadow: '0 4px 16px rgba(162,38,226,0.35)',
                                    '&:hover': { boxShadow: '0 6px 24px rgba(162,38,226,0.5)' },
                                }}
                            >
                                Новая роль
                            </Button>
                        )}
                    </Box>
                </motion.div>

                {/* Поиск */}
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.08 }}>
                    <Box sx={{ mb: 2.5 }}>
                        <Input
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            placeholder="Поиск по названию роли…"
                            startDecorator={<SearchRoundedIcon sx={{ fontSize: 16, color: 'var(--joy-palette-text-tertiary)' }} />}
                            sx={{
                                bgcolor: 'var(--joy-palette-background-level1)',
                                border: '1px solid var(--joy-palette-divider)',
                                fontSize: 13, color: 'white',
                                '&:focus-within': { borderColor: 'var(--joy-palette-primary-500)' },
                            }}
                        />
                    </Box>
                </motion.div>

                {/* Список */}
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <MemeLoader />
                    </Box>
                ) : filtered.length === 0 ? (
                    <Box sx={{
                        display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, gap: 2,
                        bgcolor: 'var(--joy-palette-background-level1)',
                        border: '1px solid var(--joy-palette-divider)',
                        borderRadius: 'var(--joy-radius-xl)',
                    }}>
                        <SecurityRoundedIcon sx={{ fontSize: 32, color: 'var(--joy-palette-text-tertiary)' }} />
                        <Typography sx={{ fontSize: 14, color: 'var(--joy-palette-text-tertiary)' }}>
                            {search ? 'Ничего не найдено' : 'Нет ролей'}
                        </Typography>
                    </Box>
                ) : (
                    <motion.div variants={stagger} initial="hidden" animate="visible">
                        <Stack spacing={1}>
                            {filtered.map(role => (
                                <RoleRow
                                    key={role.id}
                                    role={role}
                                    canCreate={canCreate}
                                    canUpdate={canUpdate}
                                    canDelete={canDelete}
                                    canAssign={canAssign}
                                    onEdit={handleOpenEdit}
                                    onDelete={(r) => setDeleteModal({ open: true, role: r, loading: false })}
                                    onDuplicate={handleDuplicate}
                                    onAssign={(r) => setAssignModal({ open: true, role: r })}
                                />
                            ))}
                        </Stack>
                    </motion.div>
                )}
            </Box>

            {/* Модалки */}
            <RoleModal
                open={editModal.open}
                role={editModal.role}
                permissions={permissions}
                loading={editModal.loading}
                error={editModal.error}
                onClose={() => setEditModal({ open: false, role: null, loading: false, error: '' })}
                onSave={handleSave}
            />

            <DeleteRoleModal
                open={deleteModal.open}
                role={deleteModal.role}
                loading={deleteModal.loading}
                onClose={() => setDeleteModal({ open: false, role: null, loading: false })}
                onConfirm={handleDelete}
            />

            <AssignRoleModal
                open={assignModal.open}
                role={assignModal.role}
                onClose={() => setAssignModal({ open: false, role: null })}
                onSaved={() => {
                    setAssignModal({ open: false, role: null });
                    loadData();
                }}
            />
        </motion.div>
    );
}